import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AccordionContentBox } from '~/components/AccordionContentBox';
import { Button } from '~/components/Button';
import { Table } from '~/components/Table';
import { useSort } from '~/hooks/useSort';
import { useDistributionColumns } from '~settings/modules/label/hooks/useDistributionColumns';

import { DistributionModal } from '../DistributionModal';
import type { DistributionGroup } from '../LabelForm/schema';

interface ModalState {
  isOpen: boolean;
  editIndex?: number;
  editValue?: DistributionGroup;
}

export function DistributionGroups() {
  const { t } = useTranslation('settings');
  const { control } = useFormContext();

  const [seeMore, setSeeMore] = useState(false);
  const { sorting, setSorting } = useSort();

  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'distributionGroups',
  });

  const closeModal = () => {
    setModalState({ isOpen: false });
  };

  const openModal = (index?: number) => {
    setModalState({
      isOpen: true,
      editIndex: index,
      editValue:
        index !== undefined
          ? (fields[index] as unknown as DistributionGroup)
          : undefined,
    });
  };

  const columns = useDistributionColumns(remove, openModal);

  const handleModalSubmit = (data: DistributionGroup) => {
    if (modalState.editIndex !== undefined) {
      update(modalState.editIndex, data);
    } else {
      append(data);
    }

    closeModal();
  };

  const rows = (seeMore || fields.length <= 10
    ? fields
    : fields.slice(0, 10)) as unknown as DistributionGroup[];

  return (
    <>
      <DistributionModal
        isOpen={modalState.isOpen}
        onOpenChange={(isOpen) => (isOpen ? openModal() : closeModal())}
        onSubmit={handleModalSubmit}
        value={modalState.editValue}
      />

      <AccordionContentBox
        title={t('label.distributionGroups')}
        actions={
          fields.length > 0 ? (
            <Button
              variant="secondary"
              size="small"
              className="font-semibold"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openModal();
              }}
            >
              {t('label.createGroup')}
            </Button>
          ) : null
        }
        defaultOpen={false}
        onValueChange={(el) => !el && setSeeMore(false)}
      >
        {fields.length > 0 ? (
          <div className="flex flex-col items-center gap-xs">
            <Table
              data={rows}
              columns={columns}
              className="border-spacing-y-md pt-0"
              rowClassName="bg-level-03 odd:bg-level-03"
              cellClassName="p-sm"
              sort={{
                sorting,
                onSortingChange: setSorting,
              }}
              manualSorting={false}
            />
            {fields.length > 10 && (
              <Button
                variant="secondary"
                size="small"
                className="font-semibold"
                type="button"
                onClick={() => setSeeMore(!seeMore)}
              >
                {seeMore ? t('showLess') : t('showMore')}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex w-full flex-col items-center gap-lg rounded-sm px-md py-2xl bg-level-03">
            <span className="text-body-md font-semibold leading-tight">
              {t('label.addDistributionGroup')}
            </span>
            <Button
              variant="secondary"
              size="small"
              className="font-semibold"
              type="button"
              onClick={() => openModal()}
            >
              {t('label.createGroup')}
            </Button>
          </div>
        )}
      </AccordionContentBox>
    </>
  );
}

DistributionGroups.displayName = 'DistributionGroups';
